import {
  AccessTimeOutlined,
  ApartmentOutlined,
  BusinessOutlined,
  Home,
  LocationOnOutlined,
  PublicOutlined
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { FormRowSwitch, FormSection, NumberStepper, SelectInput, TextInput } from "components";
import {
  adminModelCatalog,
  COUNTRY_CCA3,
  ENDPOINTS,
  MODEL_LABELS,
  MODELS,
  sensitivityFieldsForModels,
  TIMEZONES,
  VIRTUOSE_RTSP_METHOD_LABELS,
  VIRTUOSE_RTSP_METHODS
} from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { useSnackbar } from "notistack";
import { FormEventHandler, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ModelTypes } from "types";

const defaultState = {
  name: "",
  address: "",
  city: "",
  startHour: "",
  endHour: "",
  country: "",
  timezone: "",
  virtuoseRTSPMethod: "",
  overallST: "",
  zoneST: "",
  gestureST: "",
  weaponST: "",
  fightST: "",
  passedOutST: "",
  abusiveST: "",
  isVirtuoseClient: "",
  virtuoseTenantID: "",
  messageGroupId: "",
  camCount: "",
  serverId: "",
  cameraUsername: "",
  cameraPassword: ""
};

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const overallSensitivity = { key: "overallST" as const, labelKey: "Overall Sensitivity" };

export default function AddSite() {
  const [state, setState] = useState({
    ...defaultState,
    country: "CAN",
    virtuoseRTSPMethod: "cameraIpAndProfile",
    timezone: "(UTC-05:00) Canada",
    overallST: "5",
    zoneST: "5",
    gestureST: "5",
    weaponST: "5",
    fightST: "5",
    startHour: "09:00",
    endHour: "18:00",
    passedOutST: "5",
    abusiveST: "5",
    camCount: "28",
    serverId: "1",
    cameraUsername: "",
    cameraPassword: "",
    isVirtuoseClient: "false"
  });
  const [enabledModels, setEnabledModels] = useState<ModelTypes[]>(() => adminModelCatalog());
  const sensitivityFields = useMemo(
    () => [overallSensitivity, ...sensitivityFieldsForModels(enabledModels)],
    [enabledModels]
  );
  const [errors, setErrors] = useState(defaultState);

  const t = useTranslation();
  const navigate = useNavigate();

  const disabledSubmit = useMemo(
    () =>
      !!(
        errors.name ||
        !state.name.trim() ||
        errors.address ||
        !state.address.trim() ||
        errors.city ||
        !state.city.trim() ||
        Object.values(errors).some((elm) => elm)
      ),
    [state, errors]
  );

  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<{ _id: string }>("POST");
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.sites, {
        body: JSON.stringify({
          ...state,
          enabledModels
        })
      }).catch(console.error);
    },
    [state, enabledModels, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      if (response.data) {
        navigate(`/sites/${response.data._id}`);
      } else {
        navigate(-1);
      }
    }
  }, [response, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  const setSensitivity = useCallback((key: keyof typeof defaultState, value: number) => {
    setState((prev) => ({ ...prev, [key]: String(value) }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  }, []);

  return (
    <PrivateLayout canAccess={(type) => type === "super"}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Tooltip title={t["Home"]}>
          <IconButton
            component={Link}
            to="/"
            size="small"
            sx={{
              color: COLORS.surface.secondaryText,
              border: `1px solid ${COLORS.surface.border}`,
              borderRadius: `${COLORS.radius.pill}px`,
              bgcolor: COLORS.surface.card,
              width: 32,
              height: 32
            }}
          >
            <Home sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Button
          component={Link}
          to="/sites"
          sx={{ color: COLORS.surface.secondaryText, textTransform: "none", fontWeight: 500, minWidth: 0, p: 0 }}
        >
          {t["Sites"]}
        </Button>
        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
          {t["Add Site"]}
        </Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography
          variant="h1"
          sx={{ color: COLORS.surface.primaryText, fontSize: { xs: 24, md: 28 }, fontWeight: 600, mb: 0.5 }}
        >
          {t["Add Site"]}
        </Typography>
        <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 15, maxWidth: 560 }}>
          {t["Add Site description"]}
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSubmit} width="100%">
        <FormSection title={t["Site Information"]} description={t["Site Information description"]}>
          <Stack spacing={2.5}>
            <TextInput
              value={state.name}
              label={t["Name"]}
              type="text"
              placeholder={t["e.g NeuraVue Dubai Office"]}
              error={!!errors.name}
              helperText={errors.name}
              startAdornment={<BusinessOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prev) => ({ ...prev, name: e.target.value }));
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              fullWidth
              required
              autoFocus
            />
            <TextInput
              value={state.address}
              label={t["Address"]}
              type="text"
              placeholder={t["e.g Apartment/Building A12X, Street 123"]}
              error={!!errors.address}
              helperText={errors.address}
              startAdornment={<LocationOnOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prev) => ({ ...prev, address: e.target.value }));
                setErrors((prev) => ({ ...prev, address: "" }));
              }}
              fullWidth
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextInput
                  value={state.city}
                  label={t["City"]}
                  type="text"
                  placeholder={t["e.g Dubai"]}
                  error={!!errors.city}
                  helperText={errors.city}
                  startAdornment={<ApartmentOutlined sx={iconSx} />}
                  sx={fieldSx}
                  onChange={(e) => {
                    setState((prev) => ({ ...prev, city: e.target.value }));
                    setErrors((prev) => ({ ...prev, city: "" }));
                  }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInput
                  id="country-select"
                  data={COUNTRY_CCA3}
                  value={state.country}
                  label={t["Country"]}
                  helperText=""
                  startAdornment={<PublicOutlined sx={iconSx} />}
                  onChange={({ target: { value } }) => {
                    if (!value) return;
                    setState((prev) => ({ ...prev, country: value as string }));
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInput
                  id="timezone-select"
                  data={TIMEZONES}
                  value={state.timezone}
                  label={t["Time Zone"]}
                  helperText=""
                  startAdornment={<AccessTimeOutlined sx={iconSx} />}
                  onChange={({ target: { value } }) => {
                    setState((prev) => ({ ...prev, timezone: value as string }));
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Stack>
        </FormSection>

        <FormSection title={t["Operating Hours"]} description={t["Operating Hours description"]}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextInput
                value={state.startHour}
                label={t["Start Time"]}
                type="time"
                error={!!errors.startHour}
                helperText={errors.startHour}
                startAdornment={<AccessTimeOutlined sx={iconSx} />}
                sx={fieldSx}
                onChange={({ target: { value } }) => {
                  setState((prev) => ({ ...prev, startHour: value }));
                  setErrors((prev) => ({ ...prev, startHour: "" }));
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                value={state.endHour}
                label={t["End Time"]}
                type="time"
                error={!!errors.endHour}
                helperText={errors.endHour}
                startAdornment={<AccessTimeOutlined sx={iconSx} />}
                sx={fieldSx}
                onChange={({ target: { value } }) => {
                  setState((prev) => ({ ...prev, endHour: value }));
                  setErrors((prev) => ({ ...prev, endHour: !!value ? "" : t["Ending hours required"] }));
                }}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title={t["Detection Sensitivity"]} description={t["Detection Sensitivity description"]}>
          <Grid container spacing={2}>
            {sensitivityFields.map(({ key, labelKey }) => (
              <Grid item xs={12} sm={6} key={key}>
                <NumberStepper
                  label={t[labelKey]}
                  value={Number.parseInt(state[key] || "5", 10)}
                  min={1}
                  max={10}
                  error={errors[key]}
                  onChange={(value) => setSensitivity(key, value)}
                />
              </Grid>
            ))}
          </Grid>
        </FormSection>

        <FormSection title={t["Detection Features"]} description={t["Detection Features description"]}>
          <Stack divider={<Divider sx={{ borderColor: COLORS.surface.border }} />}>
            {adminModelCatalog().map((model) => (
              <FormRowSwitch
                key={model}
                label={t[MODEL_LABELS[MODELS.indexOf(model)]]}
                checked={enabledModels.includes(model)}
                onChange={(checked) => {
                  setEnabledModels((prev) => (checked ? [...prev, model] : prev.filter((item) => item !== model)));
                }}
              />
            ))}
          </Stack>
        </FormSection>

        <FormSection title={t["Client Configuration"]} description={t["Client Configuration description"]}>
          <FormRowSwitch
            label={t["Virtuose Client"]}
            checked={state.isVirtuoseClient === "true"}
            onChange={(checked) =>
              setState((prev) => ({
                ...prev,
                isVirtuoseClient: String(checked)
              }))
            }
          />
          {state.isVirtuoseClient === "true" && (
            <Fragment>
              <Divider sx={{ borderColor: COLORS.surface.border, my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextInput
                    value={state.virtuoseTenantID}
                    label={t["Virtuose Tenant ID"]}
                    type="text"
                    placeholder="e.g Groupvite"
                    error={!!errors.virtuoseTenantID}
                    helperText={errors.virtuoseTenantID}
                    sx={fieldSx}
                    onChange={({ target }) => {
                      setState((prev) => ({ ...prev, virtuoseTenantID: target.value }));
                      setErrors((prev) => ({ ...prev, virtuoseTenantID: "" }));
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    value={state.messageGroupId}
                    label={t["Virtuose Message Group Id"]}
                    type="text"
                    placeholder="e.g Groupvite"
                    error={!!errors.messageGroupId}
                    helperText={errors.messageGroupId}
                    sx={fieldSx}
                    onChange={({ target }) => {
                      setState((prev) => ({ ...prev, messageGroupId: target.value }));
                      setErrors((prev) => ({ ...prev, messageGroupId: "" }));
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    value={state.camCount}
                    label={t["Camera Count"]}
                    type="number"
                    error={!!errors.camCount}
                    helperText={errors.camCount}
                    sx={fieldSx}
                    onChange={({ target }) => {
                      setState((prev) => ({ ...prev, camCount: target.value }));
                      setErrors((prev) => ({ ...prev, camCount: "" }));
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    value={state.serverId}
                    label={t["Server ID"]}
                    type="number"
                    placeholder="e.g 1"
                    error={!!errors.serverId}
                    helperText={errors.serverId}
                    sx={fieldSx}
                    onChange={({ target }) => {
                      setState((prev) => ({ ...prev, serverId: target.value }));
                      setErrors((prev) => ({ ...prev, serverId: "" }));
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    value={state.cameraUsername}
                    label={t["Camera Username"]}
                    type="text"
                    placeholder="e.g groupvite"
                    error={!!errors.cameraUsername}
                    helperText={errors.cameraUsername}
                    sx={fieldSx}
                    onChange={({ target }) => {
                      setState((prev) => ({ ...prev, cameraUsername: target.value }));
                      setErrors((prev) => ({ ...prev, cameraUsername: "" }));
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    required
                    fullWidth
                    id="camera-password"
                    name="camera-password"
                    type="password"
                    autoComplete="new-password"
                    label={t["Camera Password"]}
                    value={state.cameraPassword}
                    helperText={errors?.cameraPassword}
                    error={!!errors?.cameraPassword}
                    sx={fieldSx}
                    onChange={(e) => {
                      setState((prev) => ({ ...prev, cameraPassword: e.target.value }));
                      setErrors((prev) => ({ ...prev, cameraPassword: "" }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SelectInput
                    id="virtuoseRTSPMethod-select"
                    data={VIRTUOSE_RTSP_METHODS.map((elm) => ({
                      label: t[VIRTUOSE_RTSP_METHOD_LABELS[elm]],
                      value: elm
                    }))}
                    value={state.virtuoseRTSPMethod}
                    label={t["RTSP Method"]}
                    helperText=""
                    onChange={({ target: { value } }) => {
                      setState((prev) => ({ ...prev, virtuoseRTSPMethod: value as string }));
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Fragment>
          )}
        </FormSection>

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1} mb={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate("/sites")}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={disabledSubmit}
            endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              color: COLORS.white,
              backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
              boxShadow: "none",
              "&:hover": {
                backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                filter: "brightness(1.05)",
                boxShadow: "none"
              }
            }}
          >
            {t["Submit"]}
          </Button>
        </Box>
      </Box>
    </PrivateLayout>
  );
}
